import { combineReducers, configureStore } from "@reduxjs/toolkit"
import { persistReducer, persistStore } from "redux-persist"
import storage from "redux-persist/lib/storage" // Defaults to localStorage for web
import userReducer from "@/store/currentUserSlice" // Import the user slice
import sessionReducer from "@/store/sessionSlice" // Import the session slice
import sortReducer from "@/store/sortSlice" // Import the sort slice
import callingLeadReducer from "@/store/calling-lead"
import callRingDuration from "@/store/call-ring-duration"
import callDocumenter from "@/store/call-documenter"

import callList from "@/store/call-list"
import liveCallDialer from "@/store/liveCallDialer"
import chatReducer from "@/store/features/chatSlice" // Import the chat slice
import todoFormReducer from "@/store/features/todoFormSlice" // Import the todoForm slice
import emailFormReducer from "@/store/features/emailFormSlice" // Import the emailForm slice
import noteFormReducer from "@/store/features/noteFormSlice" // Import the noteForm slice
import contactFormReducer from "@/store/features/contactFormSlice" // Import the contactForm slice
import calendarEventsReducer from "@/store/features/calendarEventsSlice" // Import the calendarEvents slice
import dialogReducer from "@/store/dialogSlice" // Import the dialog slice
import stOfferFormReducer from "@/store/features/stOfferFormSlice" // Import the ST offer form slice
import optimisticHistoryReducer from "@/store/features/optimisticHistorySlice" // Import the optimistic history slice
import topBarAlertsReducer from "@/store/features/topBarAlertsSlice" // Import the top-bar alerts slice
import { CallSlice } from "@/store/call"
import { dashboardSlice } from "@/store/dashboardSlice"
import type { TypedUseSelectorHook } from "react-redux"
import { useDispatch, useSelector, useStore } from "react-redux"

const persistConfig = {
  key: "persist", // Key for the persisted data
  blacklist: [
    // Don't persist real-time/sensitive slices
    "callingLead",
    "call",
    "callRingDuration",
    "callDocumenter",

    "chat",
    "todoForm",
    "emailForm",
    "noteForm",
    "contactForm",
    "calendarEvents",
    "dialog",
    "session", // Don't persist session - rely on actual server session
    "stOfferForm", // Don't persist ST offer form
    "optimisticHistory", // Don't persist optimistic history
    "dashboard", // Don't persist dashboard state - always re-fetch
    "topBarAlerts", // Always fetched fresh via useTopBarAlertsSync
    "liveCallDialer", // Real-time stream, always re-fetched on socket reconnect
  ], // Key of the slices to exclude from persistence
  storage, // Use localStorage
}

// Combine reducers
const rootReducer = combineReducers({
  user: userReducer, // User slice (NOT persisted - always fresh)
  session: sessionReducer, // Session slice (persisted - just tracks if session exists)
  sort: sortReducer, // Sort slice (persisted - user preferences)
callingLead: callingLeadReducer,
  callRingDuration: callRingDuration,
  callDocumenter: callDocumenter,

  call: CallSlice.reducer,
  callList: callList,
  liveCallDialer: liveCallDialer,
  chat: chatReducer, // Chat slice
  todoForm: todoFormReducer, // TodoForm slice
  emailForm: emailFormReducer, // EmailForm slice
  noteForm: noteFormReducer, // NoteForm slice
  contactForm: contactFormReducer, // ContactForm slice
  calendarEvents: calendarEventsReducer, // CalendarEvents slice
  dialog: dialogReducer, // Dialog slice
  stOfferForm: stOfferFormReducer, // ST offer form slice
  optimisticHistory: optimisticHistoryReducer, // Optimistic history slice
  dashboard: dashboardSlice.reducer, // Dashboard builder slice
  topBarAlerts: topBarAlertsReducer, // Top-bar alerts slice (calls/emails/todos/chat per membership)
})

// Make configured store
const makeConfiguredStore = () => {
  return configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false // Disable serializable check for redux-persist
      })
  })
}

interface PersistedStore extends ReturnType<typeof makeConfiguredStore> {
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  __persistor?: any
  /* eslint-enable  @typescript-eslint/no-explicit-any */
}

// Export a function to create a store
export const makeStore = (): PersistedStore => {
  const isServer =
    process.env.NODE_ENV === "production"
      ? typeof window === "undefined"
      : typeof window !== "object"

  if (isServer) {
    // If on server, return a configured store without persisting
    return makeConfiguredStore() as PersistedStore
  } else {
    // If on client, create a persisted reducer
    const persistedReducer = persistReducer(persistConfig, rootReducer)
    const store = configureStore({
      reducer: persistedReducer,
      middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
          serializableCheck: false // Disable serializable check for redux-persist
        })
    })

    // Create a persistor
    ;(store as PersistedStore).__persistor = persistStore(store) // Add persistor to the store
    return store as PersistedStore
  }
}

// Define inferred types for store, state, and dispatch
export type AppStore = ReturnType<typeof makeStore> // Use the makeStore function to infer the type
export type RootState = ReturnType<AppStore["getState"]>
export type AppDispatch = AppStore["dispatch"]

// Create an instance of the store
export const store = makeStore()

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = () => useDispatch<AppDispatch>()
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector
export const useAppStore = useStore.withTypes<AppStore>()
